import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Play } from 'lucide-react';

const DemoVideoPopup: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isExpanded]);

  useEffect(() => {
    const collapseTimer = setTimeout(() => {
      setIsCollapsed(true);
    }, 5000);

    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 1.5) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(collapseTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsExpanded(false);
    }
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  return (
    <>
      <style>
        {`
          @keyframes sophisticatedGlow {
            0%, 100% {
              box-shadow: 
                0 0 12px rgba(121, 227, 150, 0.3),
                0 0 24px rgba(121, 227, 150, 0.1),
                0 0 36px rgba(121, 227, 150, 0.05),
                0 4px 15px rgba(0, 0, 0, 0.1);
            }
            50% {
              box-shadow: 
                0 0 18px rgba(121, 227, 150, 0.4),
                0 0 32px rgba(121, 227, 150, 0.2),
                0 0 48px rgba(121, 227, 150, 0.1),
                0 6px 20px rgba(0, 0, 0, 0.15);
            }
          }
        `}
      </style>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.6, ease: "easeOut" }}
        className="fixed top-[calc(100vh-6.5rem)] sm:top-[calc(100vh-5.5rem)] md:top-[calc(100vh-5.5rem)] lg:top-[calc(100vh-5.5rem)] left-4 sm:left-6 md:left-8 z-40"
      >
        <motion.div 
          onClick={handleExpand}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          className="relative bg-white rounded-2xl cursor-pointer transition-all duration-500 hover:scale-105 group overflow-hidden"
          style={{
            animation: 'sophisticatedGlow 4s ease-in-out infinite'
          }}
          animate={{
            width: (isCollapsed && !isHovering) ? '3.5rem' : '20rem',
            height: (isCollapsed && !isHovering) ? '3.5rem' : 'auto'
          }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="bg-white rounded-2xl border border-gray-100 h-full">
            {(isCollapsed && !isHovering) ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="bg-gradient-to-r from-primary to-accent rounded-full p-2 group-hover:scale-110 transition-transform duration-200">
                  <Play className="w-4 h-4 text-white ml-0.5" />
                </div>
              </div>
            ) : (
              <div className="p-3">
                <div className="flex items-center space-x-3 whitespace-nowrap">
                  <div className="bg-gradient-to-r from-primary to-accent rounded-full p-2 group-hover:scale-110 transition-transform duration-200 flex-shrink-0">
                    <Play className="w-4 h-4 text-white ml-0.5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Check our demo video
                    </h3>
                    <p className="text-xs text-gray-500">
                      See Kexa in action
                    </p>
                  </div>
                  <div className="relative w-16 h-10 bg-gradient-to-br from-primary/20 to-accent/20 rounded-lg overflow-hidden flex-shrink-0">
                    <video
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    >
                      <source src="/demos/demo_kexa.mp4#t=0.1" type="video/mp4" />
                    </video>
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                      <div className="bg-white/80 rounded-full p-1 group-hover:scale-110 transition-transform duration-200">
                        <Play className="w-2 h-2 text-primary ml-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={handleBackdropClick}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ 
                scale: 1,
                opacity: 1,
                width: '95vw',
                height: '95vh'
              }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
              className="relative bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <button
                onClick={() => setIsExpanded(false)}
                className="absolute top-4 right-4 z-10 bg-black/20 hover:bg-black/40 rounded-full p-2 transition-colors duration-200"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="w-full h-full flex flex-col">
                <div className="flex-1 p-4">
                  <video
                    width="100%"
                    height="100%"
                    controls
                    autoPlay
                    className="rounded-lg"
                    style={{ minHeight: '400px' }}
                  >
                    <source src="/demos/demo_kexa.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DemoVideoPopup;